import { c as _c } from "react/compiler-runtime";
import { IntroPanel } from '@/components/Atoms/IntroPanel/IntroPanel';
import { LocalizedDateRange } from '@/components/Atoms/LocalizedDateRange/LocalizedDateRange';
import { Panel } from '@/components/Atoms/Panel/Panel';
import { Prose } from '@/components/Atoms/Prose/Prose';
import { TextCenter } from '@/components/Atoms/TextCenter/TextCenter';
import { H1, H4 } from '@/components/Core/Heading';
import { CenteredPageContent } from '@/components/Layouts/CenteredPageContent';
import type { IBreadcrumbItem } from '@/components/Molecules/Breadcrumb/Breadcrumb';
import { Breadcrumb } from '@/components/Molecules/Breadcrumb/Breadcrumb';
import { CardGrid } from '@/components/Molecules/CardGrid/CardGrid';
import type { IAvailableSession } from '@/components/Organisms/EventSessionCard/EventSessionCard';
import { EventSessionCard } from '@/components/Organisms/EventSessionCard/EventSessionCard';
import useTranslation from '@/hooks/useTranslation';
import type { IImage } from '@/interfaces';
import { breakpoints } from '@/styles/media';
import type { ReactNode } from 'react';
import type { FCC } from 'react-fc';
import styled, { css } from 'styled-components';
interface IProps {
  breadcrumbs?: IBreadcrumbItem[];
  leadImage?: IImage;
  subTitle?: string;
  mainTitle: string;
  startDate: string;
  endDate: string;
  description: string;
  moreDetail?: ReactNode;
  sessions: IAvailableSession[];
  isDateProvisional: boolean;
}
export const MultiSessionEventTemplate: FCC<IProps> = t0 => {
  const $ = _c(42);
  const {
    breadcrumbs: t1,
    leadImage,
    subTitle,
    mainTitle,
    startDate,
    endDate,
    description,
    moreDetail,
    children,
    sessions,
    isDateProvisional
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const breadcrumbs = t2;
  const {
    t
  } = useTranslation("events");
  const t3 = leadImage?.baseUrl;
  const t4 = leadImage?.baseUrl;
  let t5;
  if ($[2] !== breadcrumbs) {
    t5 = <Breadcrumb items={breadcrumbs} />;
    $[2] = breadcrumbs;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== subTitle) {
    t6 = subTitle && <H4 as="span" css={"\n                                            display: block;\n                                            margin: var(--size-grid-unit) 0 var(--size-grid-half);\n                                        "}>{subTitle}</H4>;
    $[4] = subTitle;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== mainTitle) {
    t7 = <H1 as="span">{mainTitle}</H1>;
    $[6] = mainTitle;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t6 || $[9] !== t7) {
    t8 = <h1>{t6}{t7}</h1>;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== endDate || $[12] !== isDateProvisional || $[13] !== startDate) {
    t9 = !isDateProvisional && <LocalizedDateRange startDate={startDate} endDate={endDate} css={"\n                                        display: block;\n                                        margin-top: var(--size-grid-half);\n                                    "} className="cds-t-h3" />;
    $[11] = endDate;
    $[12] = isDateProvisional;
    $[13] = startDate;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8 || $[16] !== t9) {
    t10 = <div>{t8}{t9}</div>;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t4 || $[20] !== t5) {
    t11 = <Title $imageUrl={t4}>{t5}{t10}</Title>;
    $[18] = t10;
    $[19] = t4;
    $[20] = t5;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== description) {
    t12 = <p>{description}</p>;
    $[22] = description;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== moreDetail) {
    t13 = moreDetail && <p>{moreDetail}</p>;
    $[24] = moreDetail;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t12 || $[27] !== t13) {
    t14 = <IntroPanel css={"\n                            padding: var(--size-grid-unit);\n                            margin-right: var(--size-grid-half);\n                        "}><Prose $preformatted={true}>{t12}{t13}</Prose></IntroPanel>;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== t11 || $[30] !== t14) {
    t15 = <Layout>{t11}{t14}</Layout>;
    $[29] = t11;
    $[30] = t14;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  let t16;
  if ($[32] !== t15 || $[33] !== t3) {
    t16 = <Header invert={true} $imageUrl={t3}>{t15}</Header>;
    $[32] = t15;
    $[33] = t3;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== sessions || $[36] !== t) {
    t17 = <CenteredPageContent>{sessions.length > 0 ? <CardGrid min="480px">{sessions?.map(_temp)}</CardGrid> : <TextCenter>{t("Upcoming.footer_notice")}</TextCenter>}</CenteredPageContent>;
    $[35] = sessions;
    $[36] = t;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== children || $[39] !== t16 || $[40] !== t17) {
    t18 = <div>{t16}{children}{t17}</div>;
    $[38] = children;
    $[39] = t16;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  return t18;
};
const Title = styled.div<{
  $imageUrl?: string;
}>`
    padding: var(--size-grid-unit);
    ${({
  $imageUrl
}) => $imageUrl && `
            /* Darken the image (css multiple backgrounds only works with gradients, not simple colours) */
            background: linear-gradient(rgba(33,33,33,0.5),rgba(33,33,33,0.5)), url("${$imageUrl}");
            background-size: cover;
            background-position: 50% 50%;
    `};
    ${breakpoints.L`
        background: none;
        padding: 0;
    `};
    text-shadow: 0 0 2px var(--cds-color-background);
`;
const Header = styled(Panel)<{
  $imageUrl?: string;
}>`
    background: rgba(33, 33, 33, 1);
    ${({
  $imageUrl
}) => css`
        ${breakpoints.L`
            margin-bottom: var(--size-grid-double);
            padding: var(--size-grid-double) 0;
            ${$imageUrl && `
                background: linear-gradient(rgba(33,33,33,0.5),rgba(33,33,33,0.5)), url("${$imageUrl}");
                background-size: cover;
                background-position: 50% 50%;
            `}
    `}
    `};
`;
const Layout = styled.div`
    ${breakpoints.L`
        display: flex;
        gap: var(--size-grid-unit);
        padding: 0 var(--size-grid-unit);
        max-width: var(--size-maxPageWidth);
        margin: 0 auto;
        > * {
            flex: 1;
        }
    `};
`;
function _temp(eventSession) {
  return <EventSessionCard key={eventSession.item.id} {...eventSession} />;
}